/** CONSTANTS */
import { colors } from 'constants/theme';

/** STYLE */
import { Box, ClickAwayListener, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Droppable } from "react-beautiful-dnd";
import Loading from 'component/Loading';

const SCROLL_BOTTOM_THRESHOLD = 120

export type DoctorSchedule = {
  doctorScheduleId: number
  doctorId: number
  doctorFullname: string
  timeStart: string
  timeEnd: string
}

export const SliderItem = styled('div')(({ theme }) => ({
  flex: '0 0 323px',
  width: 323,
  maxWidth: 323,
  border: `1px solid ${colors.extraLightGray}`,
  background: colors.servicePoint.panel,
  borderRadius: 8,
  [theme.breakpoints.down('sm')]: {
    flex: '0 0 277px',
    width: 277,
    maxWidth: 277
  },
}))

export const Header = styled('div')(({ theme }) => ({
  backgroundColor: '#fff !important',
  top: '0',
  zIndex: '2',
  position: 'sticky',
  padding: '0.25rem 0.5rem',
  minHeight: 56,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  borderTopLeftRadius: 8,
  borderTopRightRadius: 8,
  fontSize: 20,
  fontWeight: 600,
  color: colors.white,
  'span::selection': {
    background: 'transparent'
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: 48,
    fontSize: 18
  }
}))

export const Body = styled('div')(({ theme }) => ({
  paddingBottom: 'calc(1.5rem + 200px)',
  height: '100%'

}))

type ServicePoint = {
  headerName: string
  serviceType: string
  servicePointId?: number
  body?: any
  count?: number
  type?: 'home' | 'follow-up' | any,
  backgroundHeader?: string
  isLoading?: boolean
  showNoMoreData?: boolean
  noMoreDataText?: string
  /** เรียกเมื่อ scroll ถึงใกล้ล่างของคอลัมน์ (ใช้ lazy load แต่ละ card) */
  onScrollBottom?: () => void
  hasMoreData?: boolean
  doctorSchedules?: DoctorSchedule[]
}

/** Picks the doctor whose shift currently brackets `now`; falls back to earliest upcoming. */
const pickPrimaryDoctor = (schedules: DoctorSchedule[], now: string): DoctorSchedule | undefined => {
  if (!schedules?.length) return undefined
  const active = schedules.find(s => s.timeStart <= now && now <= s.timeEnd)
  return active || schedules[0]
}

//
export default function ServicePointsArea(props: ServicePoint) {
  const backgroundType = (type: string) => {
    if (type === 'COUNTER' || type === 'FOLLOW_UP') return colors.servicePoint.counter
    else if (type === 'ROOMSERVICES' || type === 'CLEAR' || type === 'CONSULT' || type === 'PLAN' || type === 'TREATMENT' || type === 'WAITINGTREATMENT') return colors.servicePoint.roomServices
    else if (type === 'PAYMENT') return colors.servicePoint.payment
    else return props?.backgroundHeader || colors.extraLightGray
  }

  const headDrag = `${props?.servicePointId || ''}`
  /** หมอประจำห้องจะโชว์เฉพาะการ์ดประเภทห้องบริการบนหน้าหลัก เพื่อกัน Counter/Payment เผลอแสดงผล */
  const showDoctorRow = props.type === 'home' && (props.serviceType === 'ROOMSERVICES' || props.serviceType === 'TREATMENT' || props.serviceType === 'CONSULT' || props.serviceType === 'PLAN' || props.serviceType === 'WAITINGTREATMENT' || props.serviceType === 'CLEAR')
  const [doctorPopoverOpen, setDoctorPopoverOpen] = useState(false)

  const doctorInfo = useMemo(() => {
    if (!showDoctorRow) return null
    const schedules = props.doctorSchedules || []
    if (schedules.length === 0) {
      return { empty: true as const }
    }
    const now = new Date().toTimeString().slice(0, 5) // HH:mm
    const primary = pickPrimaryDoctor(schedules, now)!
    return {
      empty: false as const,
      primary,
      total: schedules.length,
      othersCount: schedules.length - 1,
      allSchedules: schedules
    }
  }, [showDoctorRow, props.doctorSchedules])

  const doctorTooltipContent = doctorInfo && !doctorInfo.empty ? (
    <Box sx={{ minWidth: 220, py: '4px' }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1F1F1F', mb: '8px' }}>
        หมอลงเวรวันนี้ ({doctorInfo.total} คน)
      </Typography>
      {doctorInfo.allSchedules.map((s) => (
        <Box key={s.doctorScheduleId} sx={{ mb: '8px', '&:last-of-type': { mb: 0 } }}>
          <Typography sx={{ fontSize: 14, color: '#1F1F1F', lineHeight: 1.3, wordBreak: 'break-word' }}>
            {(s.doctorFullname || '').trim() || '-'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#8C8C8C', mt: '2px' }}>
            <AccessTimeIcon sx={{ fontSize: 14 }} />
            <Typography sx={{ fontSize: 13, lineHeight: 1.3 }}>
              {s.timeStart}-{s.timeEnd}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  ) : null

  return (
    <SliderItem className={'slider-area'}>
      <Header style={{ background: backgroundType(props.serviceType) }}>
        <span className={'text-ellipsis'}>{props.headerName} {props.count && `(${props.count})`}</span>
        {showDoctorRow && doctorInfo && (
          doctorInfo.empty ? (
            <Box sx={{ fontSize: 12, fontWeight: 400, fontStyle: 'italic', opacity: 0.85, mt: '2px' }}>
              ไม่มีหมอประจำห้อง
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', fontSize: 12, fontWeight: 400, mt: '2px', width: '100%', px: '4px' }}>
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flexShrink: 1 }}>
                {(doctorInfo.primary.doctorFullname || '').trim() || '-'}
              </Box>
              <Box component="span" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                · {doctorInfo.primary.timeStart}-{doctorInfo.primary.timeEnd}
              </Box>
              {doctorInfo.othersCount > 0 && (
                <ClickAwayListener onClickAway={() => setDoctorPopoverOpen(false)}>
                  <Tooltip
                    open={doctorPopoverOpen}
                    onClose={() => setDoctorPopoverOpen(false)}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    title={doctorTooltipContent}
                    arrow
                    placement="bottom"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: '#fff',
                          color: '#1F1F1F',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.16)',
                          borderRadius: '8px',
                          padding: '12px 14px',
                          maxWidth: 320
                        }
                      },
                      arrow: { sx: { color: '#fff' } }
                    }}
                  >
                    <Box
                      component="span"
                      onClick={() => setDoctorPopoverOpen(o => !o)}
                      sx={{ background: 'rgba(255,255,255,0.25)', borderRadius: '10px', px: '6px', py: '1px', fontSize: 11, lineHeight: 1.4, flexShrink: 0, cursor: 'pointer', userSelect: 'none' }}
                    >
                      +{doctorInfo.othersCount}
                    </Box>
                  </Tooltip>
                </ClickAwayListener>
              )}
            </Box>
          )
        )}
      </Header>
      <BodyDroppable type={props?.type} headerName={headDrag} body={props?.body} isLoading={props?.isLoading} showNoMoreData={props?.showNoMoreData} noMoreDataText={props?.noMoreDataText} onScrollBottom={props?.onScrollBottom} hasMoreData={props?.hasMoreData} />
    </SliderItem>
  )
}

interface BodyDroppableProps {
  type?: 'home' | 'follow-up' | any
  headerName: string
  body?: any
  isLoading?: boolean
  showNoMoreData?: boolean
  noMoreDataText?: string
  onScrollBottom?: () => void
  hasMoreData?: boolean
}

const BodyDroppable = (props: BodyDroppableProps) => {
  const [bodyEl, setBodyEl] = useState<HTMLDivElement | null>(null)
  const onScrollBottomRef = useRef(props.onScrollBottom)
  const hasMoreDataRef = useRef(props.hasMoreData)
  onScrollBottomRef.current = props.onScrollBottom
  hasMoreDataRef.current = props.hasMoreData

  useEffect(() => {
    const el = bodyEl
    if (!el || !props.onScrollBottom) return
    let ticking = false
    const handleScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        ticking = false
        if (hasMoreDataRef.current === false) return
        const { scrollTop, scrollHeight, clientHeight } = el
        if (scrollTop + clientHeight >= scrollHeight - SCROLL_BOTTOM_THRESHOLD) {
          onScrollBottomRef.current?.()
        }
      })
    }
    el.addEventListener('scroll', handleScroll, { passive: true })
    return () => el.removeEventListener('scroll', handleScroll)
  }, [bodyEl, props.onScrollBottom])

  if (props.type === 'home' || props.type === 'follow-up') {
    return (
      <Droppable droppableId={`${props.headerName}`}>
        {(dropProvided) => {
          return (
            <Body
              ref={(el) => {
                dropProvided.innerRef(el)
                setBodyEl(el)
              }}
              {...dropProvided.droppableProps}
              className={'custom-scroll'}
              style={{ overflowY: 'auto', maxHeight: '100%' }}
            >
              {props.body}
            {props.isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <Loading show={true} type="softLoading" width="28px" height="28px" />
              </Box>
            )}
            {props.showNoMoreData && !props.isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {props.noMoreDataText ?? 'ไม่มีข้อมูลเพิ่มเติมแล้ว'}
                </Typography>
              </Box>
            )}
          </Body>
          )
        }}
      </Droppable>
    )
  } else {
    return (
      <Body
        className={'custom-scroll'}
        style={{
          overflowY: 'auto',
          paddingBottom: "1.5rem",
          height: 'calc(100% - 56px)'
        }}>
        {props.body}
      </Body>
    )
  }
}